// Scatter 累積計量面板（免費遊戲）— 依 Unity LandscapeUI.prefab 的 Collect 群「FreeSpinIngo」精確還原版面。
//
// 版面（Unity 面板設計座標 884×100，aPos (0, 488.1) 掛畫面頂部中央；原點=面板中心，y 軸向上；
// 本檔以 unityScaleLandscape 等比縮放、y 反向對映 PixiJS）：
//   最左等級徽章(-364) → [爪-255.9][爪-184.4] → 獎勵面板 X3(-99.9) → [爪-16.2][爪54.5] → 獎勵面板 X5/+3(142.1)
//   → [爪224.1][爪295] → 獎勵面板 +8(380.7)。每 2 爪 + 1 獎勵面板為一組，對應 Lv2/Lv3/Lv4 三個升級門檻。
//   爪子空格 pic_claw_no → 收集後 pic_claw_up；等級徽章 tx_LV1/2/3 + tx_MAX；獎勵面板底圖 pic_collectFG1
//   （第 3 個獎勵面板 Unity 無底圖，僅顯示 +8 字圖）。
//   「+3 旋轉」「+8 旋轉」為 Unity 本地化字圖 tx_add3Spins/tx_add5Spins（後者檔名為命名遺留、內容實為
//   +8 旋轉，已逐檔核對）：add3Spins 絕對 (135.6, 27.6)、add8Spins 絕對 (397.0, 2.6)、size 138×48。
//
// 每收集一顆 Scatter（對照 Unity GamePlayUI.SetCollect）：子彈自盤面飛入對應爪槽 → 該槽 ani_collectSS 骨骼
// 播 `appear` 接 `loop`（循環中的動態爪）→ 下一次收集時，前一槽播 `fire_disappear` 並換成靜態 pic_claw_up；
// 滿 6 槽時最後一槽直接靜態化（Unity 註解「滿等後不顯示」）。等級徽章同步更新。
// 進場由上方滑入（對照 Unity ToFg 的 DOMove().From(y=591)→488）；主遊戲期間隱藏。
//
// X3/X5 提示是 TMP **精靈字**（見下方 MULTIPLIER_FONT_SIZE 註解），以 tx_Multiplier 位圖字圖集還原。

import { Container, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import { animationDuration, createEffectSpine, EFFECT_ANIM } from '../../assets/EffectSpines'
import { BitmapNumber } from '../BitmapNumber'
import { getUiTexture, type UiKey } from '../../assets/UiTextures'
import { SCATTER_COLLECTOR_SLOTS } from '../../constants'
import { killTweensOf, tweenTo } from '../tween'

// ── Unity 面板設計座標（884×100，原點=面板中心，y 向上；換算 PixiJS 時 y 取負） ──
const PANEL_AX = 0
const PANEL_AY = 488.1
const PANEL_W = 884
const PANEL_H = 100
/** 直版覆寫（PoriraitUI FreeSpinIngo：aPos (−0.9, 488.9)、scale (1.2, 1.2)）。 */
const PANEL_P_AX = -0.9
const PANEL_P_AY = 488.9
const PANEL_P_SCALE = 1.2
const BADGE_W = 120
const BADGE_H = 44
const REWARD_BG_W = 104
const REWARD_BG_H = 56
/** 6 個爪槽 x（Unity Point~Point_5，y 皆 0）、槽尺寸 66×66。 */
const SLOT_XS: readonly number[] = [-255.9, -184.4, -16.2, 54.5, 224.1, 295]
const SLOT_SIZE = 66
/** 等級徽章位置（四級重疊，擇一顯示）。 */
const BADGE_POS = { x: -364, y: 0.7 } as const
/** 三個獎勵資訊面板底圖中心（Unity Level_1_Info / Level2_Info / Level3_Info；第 3 個無底圖）。 */
const REWARD_BG: readonly { readonly x: number; readonly y: number; readonly hasBg: boolean }[] = [
  { x: -99.9, y: 0, hasBg: true },
  { x: 142.1, y: -15.1, hasBg: true },
  { x: 380.7, y: -15.1, hasBg: false },
]
/** X3/X5 倍率提示（Unity Level_1_Info/Level2_Info 的 MultiplierText TMP）。 */
const MULTIPLIER_ITEMS: readonly { readonly text: string; readonly x: number; readonly y: number }[] = [
  { text: 'X3', x: -99.9, y: 0 },
  { text: 'X5', x: 142.0, y: -15.1 },
]
/**
 * X3/X5 字級（Unity MultiplierText 的 `m_fontSize: 50`、置中對齊）。
 *
 * ⚠️ **不可依 `m_fontColor` 上色**：該 TMP 的 `m_fontAsset` 是 `Empty.asset`（0 個 glyph），字面完全來自
 * `m_spriteAsset: tx_Multiplier` 精靈圖集，且 `m_tintAllSprites: 0` ⇒ `m_fontColor`（純白）對精靈不生效，
 * 畫面上呈現的是圖集本身的美術（白→黃→橘漸層字面＋深紅描邊）。先前曾據 `m_fontColor` 把 X3/X5 改成
 * 純白描邊文字，即為誤讀；改用同一張 tx_Multiplier 位圖字（倍率吊牌 MultiplierView 亦用此字型）。
 */
const MULTIPLIER_FONT_SIZE = 50
/**
 * 「+3 旋轉」「+8 旋轉」字圖（Unity add3Spins/add8Spins，size 138×48）。
 * 兩者都是獎勵資訊面板的子節點，座標＝面板 aPos ＋ 子節點 aPos：
 *   add3Spins = Level2_Info (142.1, −15.1) ＋ (−6.5, 42.7) → (135.6, 27.6)
 *   add8Spins = Level3_Info (380.7, −15.1) ＋ (16.3, 17.7) → (397.0, 2.6)
 * [修正 2026-07-28] add8Spins 曾被改為 x=535（誤判「實機截圖比 prefab 更右」），實測會飛出
 * 884 寬面板右緣、壓在右上 WildInfoGroup（x=570.5、單列 266 寬）上。已核對 LandscapeUI.prefab／
 * PoriraitUI 變體／Scene 皆無覆寫、GamePlayUI 也不在執行期搬動它 → 一律以 prefab 值為準。
 */
const ADD_SPIN_ITEMS: readonly { readonly key: UiKey; readonly x: number; readonly y: number }[] = [
  { key: 'add3Spins', x: 135.6, y: 27.6 },
  { key: 'add8Spins', x: 397.0, y: 2.6 },
]
const ADD_SPIN_W = 138
/** 計量等級（1~4）對應的徽章貼圖 key（tx_LV1/2/3 + tx_MAX）。 */
const BADGE_KEYS: readonly UiKey[] = ['levelBadge1', 'levelBadge2', 'levelBadge3', 'levelBadgeMax']

/** 爪槽點亮彈出時長（秒）。 */
const SLOT_POP_DURATION = 0.26
/** 進場由上方滑入的位移（Unity 面板設計單位，591→488）。 */
const SLIDE_IN_DISTANCE = 103

export class CollectorView extends Container implements Resizable {
  private readonly panel = new Sprite(getUiTexture('collectBg'))
  /** 6 個爪槽（pic_claw_no ↔ pic_claw_up 切換），依 filledCount 顯示已收集/空。 */
  private readonly slots: Sprite[] = Array.from({ length: SCATTER_COLLECTOR_SLOTS }, () => new Sprite(getUiTexture('clawEmpty')))
  /** 等級徽章（tx_LV1/2/3 + tx_MAX），依當前等級切換貼圖。 */
  private readonly badge = new Sprite(getUiTexture('levelBadge1'))
  /** 三個獎勵資訊面板底圖（第 3 個 hasBg=false 隱藏）。 */
  private readonly rewardBgs: Sprite[] = REWARD_BG.map(() => new Sprite(getUiTexture('collectReward')))
  /** X3/X5 倍率提示（tx_Multiplier 位圖字）。 */
  private readonly multiplierTexts: BitmapNumber[] = MULTIPLIER_ITEMS.map((item) => {
    const value = new BitmapNumber('multiplier')
    value.setText(item.text)
    return value
  })
  /** 「+3 旋轉」「+8 旋轉」字圖。 */
  private readonly addSpinSprites: Sprite[] = ADD_SPIN_ITEMS.map((item) => new Sprite(getUiTexture(item.key)))
  /** 每槽一份 ani_collectSS 骨骼（Unity _collectPointSpine，PointSpine 183×207）；載入失敗為 null → 純靜態 fallback。 */
  private readonly slotSpines: (Spine | null)[] = Array.from({ length: SCATTER_COLLECTOR_SLOTS }, () =>
    createEffectSpine('collectSS'),
  )

  private screenW = 0
  private scaleFactor = 1
  /** 面板進場後的靜止 y（螢幕座標）；滑入/滑出以此為基準。 */
  private restY = 0
  /** 已點亮的槽數（0~SCATTER_COLLECTOR_SLOTS）。 */
  private filledCount = 0
  /** 正在播 loop 動態爪的槽 index（最新收集的槽；-1=無，滿 6 後亦為 -1）。 */
  private activeSpineIndex = -1
  /** 目前計量等級（1~4），供徽章顯示。 */
  private currentLevel = 1

  constructor() {
    super()
    this.visible = false
    this.panel.anchor.set(0.5)
    this.badge.anchor.set(0.5)
    for (const slot of this.slots) slot.anchor.set(0.5)
    for (const bg of this.rewardBgs) bg.anchor.set(0.5)
    for (const sprite of this.addSpinSprites) sprite.anchor.set(0.5)

    // z-order：面板底圖 → 獎勵面板底 → 爪槽 → 等級徽章 → X3/X5 文字 → +N 旋轉字圖 → 動態爪骨骼最上。
    this.addChild(this.panel, ...this.rewardBgs, ...this.slots, this.badge, ...this.multiplierTexts, ...this.addSpinSprites)
    for (const spine of this.slotSpines) {
      if (spine !== null) {
        spine.visible = false
        this.addChild(spine)
      }
    }
  }

  /** 進入免費遊戲：重置計量（清空爪槽、回 Lv1）並由上方滑入淡入。由 GameController 呼叫。 */
  beginFreeGame(): void {
    this.filledCount = 0
    this.activeSpineIndex = -1
    for (const spine of this.slotSpines) if (spine !== null) spine.visible = false
    this.currentLevel = 1
    this.redrawSlots()
    this.applyBadge()
    this.visible = true
    killTweensOf(this)
    this.alpha = 0
    this.position.set(this.screenW / 2, this.restY - SLIDE_IN_DISTANCE * this.scaleFactor)
    void tweenTo(this, { alpha: 1, y: this.restY, duration: 0.5, ease: 'back.out(1.3)' })
  }

  /** 離開免費遊戲：向上滑出淡出隱藏。由 GameController 呼叫。 */
  endFreeGame(): void {
    killTweensOf(this)
    void tweenTo(this, {
      alpha: 0,
      y: this.restY - SLIDE_IN_DISTANCE * this.scaleFactor,
      duration: 0.3,
    }).then(() => {
      if (!this.destroyed) this.visible = false
    })
  }

  /**
   * 點亮第 (count-1) 爪槽＋更新等級徽章。子彈飛行由 FxLayerView 負責（GameController 先
   * fx.flyBullet(來源, slotCenter) 再 await 本方法），對照 Unity GamePlayUI.SetCollect 的拆分：
   *   當前槽 spine 播 `appear`→`loop`（循環）→ 前一槽靜態圖換 claw_up、spine 播 `fire_disappear` 後隱藏
   *   → 滿 6 槽時當前槽也直接靜態化（Unity「滿等後不顯示」）。
   * count 為收集後累積點數（滿 6 後續顆維持滿格）；level 為收集後等級。
   */
  async fillSlot(count: number, level: number): Promise<void> {
    const slotIndex = Math.min(count, SCATTER_COLLECTOR_SLOTS) - 1
    this.setBadge(level)
    if (slotIndex < this.filledCount) return // 滿格後的續收集：計量不再變化（Unity _currentCollectCount>=length 直接略過）
    this.filledCount = slotIndex + 1

    const spine = this.slotSpines[slotIndex]
    if (spine !== null && spine !== undefined) {
      this.activeSpineIndex = slotIndex
      spine.visible = true
      spine.state.setAnimation(0, EFFECT_ANIM.collectPointIn, false)
      spine.state.addAnimation(0, EFFECT_ANIM.collectPointLoop, true, 0)
    } else {
      // 骨骼不可用 → 舊版靜態點亮 fallback。
      const slot = this.slots[slotIndex]
      if (slot !== undefined) {
        slot.texture = getUiTexture('clawFilled')
        slot.scale.set(this.slotScale() * 1.6)
        const target = this.slotScale()
        await tweenTo(slot.scale, { x: target, y: target, duration: SLOT_POP_DURATION, ease: 'back.out(2.4)' })
      }
    }

    // 前一槽：靜態圖點亮＋動態爪退場（await fire_disappear 播完，對照 Unity WaitUntil(track.IsComplete)）。
    const previous = slotIndex - 1
    if (previous >= 0) {
      const prevSlot = this.slots[previous]
      if (prevSlot !== undefined) prevSlot.texture = getUiTexture('clawFilled')
      const prevSpine = this.slotSpines[previous]
      if (prevSpine !== null && prevSpine !== undefined && prevSpine.visible) {
        prevSpine.state.setAnimation(0, EFFECT_ANIM.collectPointOut, false)
        await new Promise((resolve) =>
          setTimeout(resolve, animationDuration(prevSpine, EFFECT_ANIM.collectPointOut, 0.5) * 1000),
        )
        prevSpine.visible = false
      }
    }

    // 滿 6 槽：最後一槽不留動態爪，直接靜態化（Unity NOTE: 滿等後不顯示）。
    if (slotIndex === SCATTER_COLLECTOR_SLOTS - 1) {
      if (spine !== null && spine !== undefined) spine.visible = false
      this.activeSpineIndex = -1
      const slot = this.slots[slotIndex]
      if (slot !== undefined) slot.texture = getUiTexture('clawFilled')
    }
  }

  /** 第 index 爪槽中心的螢幕座標（子彈飛行終點；本容器直接以螢幕座標定位，故本地座標＋容器位移即螢幕座標）。 */
  slotCenter(index: number): { x: number; y: number } {
    const slot = this.slots[Math.max(0, Math.min(this.slots.length - 1, index))]
    return { x: this.x + (slot?.x ?? 0), y: this.y + (slot?.y ?? 0) }
  }

  /**
   * 獎勵面板提示項中心的螢幕座標（獎勵子彈飛行起點）。
   * index：0=X3（Lv2 加倍）、1=X5（Lv3 加倍）、2=+3（Lv3 加次數）、3=+8（Lv4 加次數）。
   * 對照 Unity SpawnMultiplierBullet 的 _x3Spine/_x5Spine 與 SpawnFreeSpinBullet 的 _3SpinSpine/_8SpinSpine。
   */
  rewardItemCenter(index: number): { x: number; y: number } {
    const items = [...MULTIPLIER_ITEMS.map((i) => ({ x: i.x, y: i.y })), ...ADD_SPIN_ITEMS.map((i) => ({ x: i.x, y: i.y }))]
    const item = items[Math.max(0, Math.min(items.length - 1, index))]!
    return { x: this.x + item.x * this.scaleFactor, y: this.y - item.y * this.scaleFactor }
  }

  /** 爪槽貼圖的基準縮放（槽 66×66 設計尺寸相對貼圖原生尺寸）。 */
  private slotScale(): number {
    const texW = this.slots[0]?.texture.width ?? SLOT_SIZE
    return (SLOT_SIZE * this.scaleFactor) / texW
  }

  /**
   * 依 filledCount 重畫所有爪槽靜態圖（已收集=claw_up、未收集=claw_no），並重置為基準縮放。
   * 正在播 loop 動態爪的槽（activeSpineIndex）維持 claw_no 底圖——對照 Unity 只在「下一次收集」
   * 才把前一槽的 overrideSprite 換成 pic_claw_up。
   */
  private redrawSlots(): void {
    this.slots.forEach((slot, index) => {
      const filled = index < this.filledCount && index !== this.activeSpineIndex
      slot.texture = getUiTexture(filled ? 'clawFilled' : 'clawEmpty')
      slot.scale.set(this.slotScale())
    })
  }

  /** 設定並顯示計量等級徽章（1~4；≥4 顯示 tx_MAX）。 */
  private setBadge(level: number): void {
    this.currentLevel = Math.max(1, Math.min(BADGE_KEYS.length, Math.floor(level)))
    this.applyBadge()
  }

  /** 套用當前等級徽章貼圖並固定為 Unity 尺寸（120×44，避免各級圖原生尺寸不一）。 */
  private applyBadge(): void {
    this.badge.texture = getUiTexture(BADGE_KEYS[this.currentLevel - 1]!)
    this.badge.width = BADGE_W * this.scaleFactor
    this.badge.height = BADGE_H * this.scaleFactor
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width

    const isPortrait = orientation === 'portrait'
    // 橫版照 Unity FreeSpinIngo：等比縮放、aPos (0, 488.1)；
    // 直版照 PoriraitUI 覆寫：aPos (−0.9, 488.9)、scale ×1.2。
    this.scaleFactor = isPortrait
      ? unityScalePortrait(width, height) * PANEL_P_SCALE
      : unityScaleLandscape(width, height)
    const s = this.scaleFactor
    const center = isPortrait
      ? unityAnchorPortrait(PANEL_P_AX, PANEL_P_AY, width, height)
      : unityAnchorLandscape(PANEL_AX, PANEL_AY, width, height)
    this.restY = center.y
    this.position.set(center.x, this.restY)

    // 面板底圖（原點=面板中心）。
    this.panel.width = PANEL_W * s
    this.panel.height = PANEL_H * s

    // 6 爪槽（3 對，Unity Point 座標，66×66）；動態爪骨骼原點對齊槽中心（PointSpine aPos (0,0)）。
    this.slots.forEach((slot, index) => {
      slot.position.set((SLOT_XS[index] ?? 0) * s, 0)
      const spine = this.slotSpines[index]
      if (spine !== null && spine !== undefined) {
        spine.position.set((SLOT_XS[index] ?? 0) * s, 0)
        spine.scale.set(s)
      }
    })

    // 等級徽章（最左，四級同位）。
    this.badge.position.set(BADGE_POS.x * s, -BADGE_POS.y * s)

    // 三個獎勵資訊面板底圖。
    this.rewardBgs.forEach((bg, index) => {
      const spec = REWARD_BG[index]!
      bg.visible = spec.hasBg
      bg.width = REWARD_BG_W * s
      bg.height = REWARD_BG_H * s
      bg.position.set(spec.x * s, -spec.y * s)
    })

    // X3/X5 倍率提示（Unity fontSize 50 ⇒ 位圖字高度＝50×s，同 MultiplierView 的換算慣例）。
    const fontSize = Math.max(16, MULTIPLIER_FONT_SIZE * s)
    this.multiplierTexts.forEach((text, index) => {
      const item = MULTIPLIER_ITEMS[index]!
      text.setHeight(fontSize)
      text.position.set(item.x * s, -item.y * s)
    })

    // 「+3 旋轉」「+8 旋轉」字圖（138 寬）。
    this.addSpinSprites.forEach((sprite, index) => {
      const item = ADD_SPIN_ITEMS[index]!
      sprite.scale.set((ADD_SPIN_W * s) / sprite.texture.width)
      sprite.position.set(item.x * s, -item.y * s)
    })

    this.redrawSlots()
    this.applyBadge()
  }
}
